import { useEffect, useRef, useState } from 'react';
import classes from './slider.module.scss';

export interface ISlide {
  image: {
    path: string;
    alt: string;
  };
  title: string;
  subtitle: string;
}

interface IProps {
  slides: ISlide[];
  intervalMs?: number;
}

export const Slider = ({ slides, intervalMs = 5000 }: IProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % slides.length);
  };

  const goToIndex = (index: number) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    if (isPaused || slides.length <= 1) return undefined;

    timerRef.current = setInterval(goToNext, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, activeIndex, intervalMs, slides.length]);

  if (!slides.length) return null;

  return (
    <div
      className={classes.sliderPanel}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={classes.sliderViewport}>
        <div
          className={classes.sliderTrack}
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div className={classes.slide} key={index}>
              <div className={classes.imageWrapper}>
                <img src={slide.image.path} alt={slide.image.alt} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={classes.textWrapper}>
        <h3 className={classes.slideTitle}>{slides[activeIndex].title}</h3>
        <p className={classes.slideSubtitle}>{slides[activeIndex].subtitle}</p>
      </div>

      {slides.length > 1 && (
        <div className={classes.dots}>
          {slides.map((_, index) => (
            <button
              type="button"
              key={index}
              className={`${classes.dot} ${index === activeIndex ? classes.dotActive : ''}`}
              onClick={() => goToIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};